/*
 * � 2020 Microsoft. All rights reserved.
 */

define('SolitaireShared/CardLogic/CardAction',['lodash', 'absolute/debug', 'SolitaireShared/CardLogic/Supply', 'SolitaireShared/CardLogic/CardOp'],
function (_, Debug, Supply, CardOp) {

    var CardAction = function (cardSupply) {
        this.initCardAction(cardSupply);
    };

    CardAction.DelimiterChars = [' ', '\t', '\n'];
    CardAction.TimestampChars = ['[', ']'];

    CardAction.prototype.initCardAction = function (cardSupply) {
        this.Supply = cardSupply;
        this.Ops = [];
        this.Timestamp = -1;
        this.EffectiveScore = 0;
        this.Tag = 0;
        this.IsWildcardAction = false;
        this.isAllowed = true;
    };

    /// <summary>
    /// Parse a string containing a stream of actions into an action list, each of which contains 1 or more operations. Returns null if a parse error occurred.
    /// </summary>
    /// <param name="actionList">A string containing one or more actions.</param>
    /// <returns>Returns a list of actions, or an empty list if there was a parse error.</returns>
    CardAction.ParseActions = function (cardSupply, actionList) {
        var _Delimiter = /\s/;
        var Actions = [];
        var words = actionList ? actionList.split(_Delimiter) : [];
        var position = 0;

        while (position < words.length) {
            if (words[position].length == 0) {
                position++;
                continue;
            }
            var a = new CardAction(cardSupply);

            var newPos = a.Parse(words, position);
            if (newPos <= 0) {
                return null;
            }
            Actions.push(a);
            position += newPos;
        }

        return Actions;
    };

    /// <summary>
    /// Parse a string of actions, then run it on the given supply.
    /// </summary>
    /// <param name="cardSupply">A card supply to run the acitons on</param>
    /// <param name="actionList">A string with an action list, whitespace separated.</param>
    /// <returns>Returns true if all succeeded or false if anything failed.</returns>
    CardAction.RunActions = function (cardSupply, actionList)
    {
        //console.log("Running: " + actionList);
        var actions = CardAction.ParseActions(cardSupply, actionList);

        if (!cardSupply.SuppressAnimations && cardSupply.CardAnimator != null)
            cardSupply.CardAnimator.BeginAnimationSet(actions.length);

        for (var i = 0; i < actions.length; i++)
        {
            var action = actions[i];
            var success = action.Run();
            if (!success) 
            {
                if (!cardSupply.SuppressAnimations && cardSupply.CardAnimator != null)
                    cardSupply.CardAnimator.EndAnimationSet();

                return false;
            }
        }

        if (!cardSupply.SuppressAnimations && cardSupply.CardAnimator != null)
            cardSupply.CardAnimator.EndAnimationSet();

        return true;
    };

    CardAction.sParse = function (supply, words) {
        var action = new CardAction(supply);
        action.Parse(words, 0);
        return action;
    };

    /// <summary>
    /// Parse the next action out of the word list. Returns the next startingWord index (or the one you originally passed in if there was an error).
    /// </summary>
    /// <param name="words">List of all words to parse.</param>
    /// <param name="startingWord">Index of the word to start with.</param>
    /// <returns>Number of words consumed by this action.</returns>
    CardAction.prototype.Parse = function (words, startingWord)
    {
        startingWord = startingWord || 0;

        // op.parse through the action string until it fails or:
        // -- you get one action that is not a begin
        // -- if you did get a begin, then until you hit an end

        var multiAction = false;


        var totalWords = 0;


        if (words[startingWord][0] == "[" && words[startingWord][words[startingWord].length - 1] == "]")
        {
            // first word is a timestamp!
            var timestampStr = words[startingWord].substring(1, words[startingWord].length - 1);
            var timestamp = parseInt(timestampStr);

            if (!isNaN(timestamp))
            {
                this.Timestamp = timestamp;
            }
            startingWord++;
            totalWords++;
        }

        this.Ops = [];

        do
        {
            if (startingWord >= words.length)
            {
                return 0;
            }
            var op = new CardOp(this.Supply);
            var numWords = op.Parse(words, startingWord);
            if (numWords == 0) return 0;
            if (numWords < 0)
            {
                // If numWords < 0, there was a wildcard specified (i.e. a pile number or card count of 0). This is not entirely a vaoid action, but
                // can be used as a wildcard to query the action system for a list of potential actions.
                numWords = -numWords;
                this.IsWildcardAction = true;
            }

            if (!multiAction && op.Opcode == CardOp.Op.Begin)
            {
                multiAction = true;
            }
            else if (multiAction && op.Opcode == CardOp.Op.End)
            {
                multiAction = false;
            }
            else if (!multiAction && op.Opcode == CardOp.Op.Error)
            {
                return 0; // an error parsing!
            }
            else
            {
                this.Ops.push(op);
            }

            startingWord += numWords;
            totalWords += numWords;


        }
        while (multiAction);

        return totalWords;
    };

    CardAction.prototype.IsNewGameAction = function ()
    {
        if (this.Ops.length == 1 && this.Ops[0].Opcode == CardOp.Op.Game) return true;
        else return false;
    };

    /// <summary>
    /// Check if this action would be allowed in the current game state.
    /// </summary>
    /// <returns>True if the action would be allowed, false if not.</returns>
    CardAction.prototype.IsAllowed = function (triggerTips)
    {
        triggerTips = !!triggerTips;

        if (this.IsWildcardAction)
            return false;

        if (this.IsNewGameAction())
            return true;

        if (this.Supply.EnforceGameRules && this.Supply.Authority != null)
            return this.Supply.Authority.IsActionAllowed(this, triggerTips);

        return true;
    };

    /// <summary>
    /// Returns whether this entire action is undoable or not.
    /// </summary>
    /// <returns>Returns true if the entire action is undoable.</returns>
    CardAction.prototype.IsUndoable = function ()
    {
        for (var i = 0; i < this.Ops.length; i++)
        {
            if (!this.Ops[i].IsUndoable()) return false;
        }
        return true;
    };

    /// <summary>
    /// Returns true if this entire action is just a no-op.
    /// </summary>
    /// <returns>Returns true if this entire action is just a no-op.</returns>
    CardAction.prototype.IsNoop = function ()
    {
        if (this.Ops.length == 1 && this.Ops[0].Opcode == CardOp.Op.Noop) return true;
        else return false;
    };


    CardAction.prototype.IsMove = function ()
    {
        var i = this.Ops.length;
        while(--i > -1)
        {
            if (this.Ops[i].Opcode == CardOp.Op.Move) return true;
        }
        return false;
    };


    /// <summary>
    /// Returns true if this entire action is just an error.
    /// </summary>
    /// <returns>Returns true if this entire action is just a no-op.</returns>
    CardAction.prototype.IsError = function ()
    {
        if (this.Ops.length == 1 && this.Ops[0].Opcode == CardOp.Op.Error) return true;
        else return false;
    };

    /// <summary>
    /// Returns true if the action contains a pre-game operation (such as Deal, Option, or Game).
    /// </summary>
    /// <returns>Returns true if the action contains a pre-game operation (such as Deal, Option, or Game).</returns>
    CardAction.prototype.IsPregameAction = function ()
    {
        for (var i = 0; i < this.Ops.length; i++)
        {
            var op = this.Ops[i];
            if (op.Opcode == CardOp.Op.Deal ||
                op.Opcode == CardOp.Op.Option ||
                op.Opcode == CardOp.Op.Game)
                return true;
        }
        return false;
    };

    /// <summary>
    /// Run this action (if possible).
    /// </summary>
    /// <returns>True if the action succeeded, false if it failed.</returns>
    CardAction.prototype.Run = function (triggerTips)
    {
        triggerTips = typeof triggerTips == 'undefined' ? true : triggerTips;

        var shouldLog = true;
        var success = true;


        if (!this.IsAllowed(triggerTips)) {
            //System.Diagnostics.Debug.WriteLine("Action FORBIDDEN: '{0}'", this.ToString());
            return false;
        }

        if (this.IsPregameAction())
            shouldLog = false;
        /*
         if (shouldLog)
         {
         //System.Diagnostics.Debug.WriteLine("Action: '{0}'", this.ToString());
         }
         else
         {
         //System.Diagnostics.Debug.WriteLine("Action UNLOGGED: '{0}'", this.ToString());

         }*/

        var IsMovementAction = false;
        for (var i = 0; i < this.Ops.length; i++)
        {
            var op = this.Ops[i];
            if (op.Opcode == CardOp.Op.Move) {
                IsMovementAction = true;
                break;
            }
            else if (op.Opcode == CardOp.Op.Option) {
                if (op.GetOptionName(CardOp.eOptionParams.Option).toLowerCase() == "cumulativescore") {
                    var score = this.Supply.CumulativeScore;
                    var tmpScore = parseInt(op.GetOptionValue(CardOp.eOptionParams.Value));
                    if (this.Supply.UseCumulativeScoring && !isNaN(tmpScore))
                        this.Supply.CumulativeScore = tmpScore;
                }
            }
        }
        

        if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null && IsMovementAction) {
            this.Supply.IncrementActiveAnimations();
            if (this.Supply.CardAnimator.DoesAnimationBlockInput(this))
                this.Supply.IncrementActiveBlockingAnimations();
            this.Supply.CardAnimator.BeginAnimation(this, false);
        }

        for (var num = 0; num < this.Ops.length; num++) {
            var op = this.Ops[num];
            if (!op.Run()) // we must complete them all!
            {
                Debug.log("Warning: CardOp Run failed!");
                success = false;
            }

            if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null && IsMovementAction) {
                this.Supply.CardAnimator.ContinueAnimation(this, num);
            }
        }

        if (success && shouldLog) {
            var prevScore = this.Supply.Score;
            this.Supply.Score += this.Supply.Authority.GetScoreForAction(this);
            this.EffectiveScore = this.Supply.Score - prevScore;

            if (this.Tag != 0) {
                this.Supply.Authority.OnRunSpecialAction(this.Tag, false);
            }
            this.Supply.Authority.OnRunAction(this, false, triggerTips);
            this.Supply.LogAction(this);

            this.Supply.CumulativeScore = this.Supply.CumulativeScore + this.EffectiveScore;
        }

        if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null && IsMovementAction) {
            this.Supply.CardAnimator.EndAnimation(this, function (a) {
                if (this.Supply.CardAnimator.DoesAnimationBlockInput(this))
                    this.Supply.DecrementActiveBlockingAnimations();
                this.Supply.DecrementActiveAnimations();
                this.Supply.OnAnimationEnd(this);
            }.bind(this));
        }

        return success;
    };

    /// <summary>
    /// If this action contains undoes in it, returns the number of undoes. Otherwise returns 0.
    /// </summary>
    /// <returns>0 if not an undo action, 1 or more if it is an undo action.</returns>
    CardAction.prototype.GetActionUndoCount = function ()
    {
        var total = 0;
        _.forEach (this.Ops, function (op)
        {
            if (op.Opcode == CardOp.Op.Undo)
            {
                total = total + op.GetNumber(CardOp.eUndoParams.NumMoves);
            }
        });
        return total;
    };

    /// <summary>
    /// Undo this action (if possible).
    /// </summary>
    /// <returns>True if it was an undoable action, false if not.</returns>
    CardAction.prototype.Undo = function ()
    {
        if (!this.IsUndoable())
            return false;

        this.Supply.Authority.ResetNoMoreMovesState();

        if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null)
        {
            this.Supply.IncrementActiveAnimations();
            this.Supply.CardAnimator.BeginAnimation(this, true);
        }

        // go through in reverse!
        for (var i = this.Ops.length - 1; i >= 0; i--)
        {
            var op = this.Ops[i];
            if (!op.Undo())
            {
                Debug.log("Warning: CardOp Undo failed!");
            }

            if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null)
            {
                this.Supply.CardAnimator.ContinueAnimation(this, i);
            }
        }

        if (!this.Supply.SuppressAnimations && this.Supply.CardAnimator != null)
        {
            this.Supply.CardAnimator.EndAnimation(this, function (a) {
                this.Supply.DecrementActiveAnimations();
                this.Supply.OnAnimationEnd(this);
            }.bind(this));
        }

        return true;
    };


    CardAction.prototype.ToString = function ()
    {
        var str = "";
        if (this.Timestamp > 0)
        {
            str += "[";
            str += this.Timestamp;
            str += "] ";
        }
        if (this.Ops.length > 1)
        {
            str += "B ";
        }

        _.forEach (this.Ops, function (op)
        {
            str += op.ToString();
            str += " ";
        });

        if (this.Ops.length > 1)
        {
            str += "E ";
        }

        return str;
    };


    CardAction.prototype.GetPotentialActions = function (supply, tryAction)
    {
        var actions = [];
        if (tryAction.IsAllowed())
            actions.push(tryAction);

        // If action has wildcard entries, cycle through the operations and check 'em all out.

        // ...

        return actions;
    };

    return CardAction;

});
